"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { getCountryCallingCode } from "react-phone-number-input";
import type { Country } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

type Option = { value?: Country; label: string };

const DEFAULT_TRIGGER_CLASS =
  "flex h-full items-center gap-1.5 rounded-xl border border-[#E9EEF1] bg-white px-2.5 py-2 text-sm text-[#264653] transition hover:bg-[#FDF9F5] focus:border-[#0F888E] focus:outline-none";

/**
 * Searchable country selector for react-phone-number-input
 * (`countrySelectComponent`). Replaces the native <select>, which has no
 * search and no visual hint that it's interactive. The trigger shows
 * flag + dial code + chevron; the dropdown filters by country name (labels
 * come localized from the lib) or by dial code.
 */
export function CountrySelectWithSearch({
  value,
  onChange,
  options,
  disabled,
  triggerClassName = DEFAULT_TRIGGER_CLASS,
}: {
  value?: Country;
  onChange: (value?: Country) => void;
  options: Option[];
  disabled?: boolean;
  /** Pass through PhoneInput's `countrySelectProps` when the surrounding form
   *  uses a different input style than the booking modal's. */
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const countries = options.filter((o): o is { value: Country; label: string } =>
    Boolean(o.value),
  );
  const q = query.trim().toLowerCase();
  const filtered = q
    ? countries.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          getCountryCallingCode(o.value).startsWith(q.replace(/^\+/, "")),
      )
    : countries;

  const Flag = value ? flags[value] : undefined;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((o) => !o);
          setQuery("");
        }}
        aria-label="Alege țara pentru prefix"
        aria-expanded={open}
        className={triggerClassName}
      >
        {Flag ? (
          <span className="block w-5 overflow-hidden rounded-[2px]">
            <Flag title={value ?? ""} />
          </span>
        ) : null}
        <span className="text-xs font-semibold">
          {value ? `+${getCountryCallingCode(value)}` : "…"}
        </span>
        <ChevronDown
          className={`size-3.5 text-[#264653]/50 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-[calc(100%+4px)] z-20 w-64 overflow-hidden rounded-xl border border-[#E9EEF1] bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-[#E9EEF1] px-3 py-2">
            <Search className="size-3.5 shrink-0 text-[#264653]/40" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Caută țara sau prefixul…"
              className="w-full text-sm text-[#264653] placeholder:text-[#264653]/40 focus:outline-none"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1">
            {filtered.map((o) => {
              const OptFlag = flags[o.value];
              const active = o.value === value;
              return (
                <li key={o.value}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-[#264653] hover:bg-[#FDF9F5] ${
                      active ? "bg-[#EDF7F7] font-semibold" : ""
                    }`}
                  >
                    <span className="block w-5 shrink-0 overflow-hidden rounded-[2px]">
                      {OptFlag ? <OptFlag title={o.label} /> : null}
                    </span>
                    <span className="flex-1 truncate">{o.label}</span>
                    <span className="shrink-0 text-xs text-[#264653]/55">
                      +{getCountryCallingCode(o.value)}
                    </span>
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-[#264653]/55">Nicio țară găsită.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
